import { mediaQuery } from '@design-system/styles'
import type { DefaultProps } from '@design-system/types'
import { css } from '@emotion/react'
import { s3URL } from '@mathflat/common/util'
import React, { type ReactNode } from 'react'
import { matchPath, type NavLink, useLocation } from 'react-router-dom'

import TabNavigationLink from '@/component/atoms/NavigationLink/TabNavigationLink'
import { SIDE_NAVIGATION_WIDTH } from '@/component/organisms/Navigation/SideNavigation/SideNavigation'
import useRepository from '@/hooks/useRepository'
import { preloadModule } from '@/utils/onLinkPreload'

import PageTemplateService from './_service/PageTemplateService'
import LnbPageLayout from './LnbPageLayout'

type NavInfo =
  | {
      label: ReactNode
      to: React.ComponentProps<NavLink>['to']
      /** 활성 판별에 쓸 경로. 한 탭이 여러 라우트를 포괄하면 배열로 전달한다. */
      matchUrl?: string | string[]
      /** base 경로(예: /lesson-preparation/school-exam)가 형제 탭의 prefix 라, 정확히 일치할 때만 active 로 잡을 때 */
      exact?: boolean
      onClick?: () => void
      restrictionMessage?: string
      // 탭 진입 전 lazy 청크 프리로드용 함수. a 태그 속성으로 새지 않게 WithTab 에서 분해해 소비한다.
      importStatement?: () => Promise<unknown>
      isNew?: boolean
    }
  | false
  | undefined

const PageContentTemplate = ({ children }: DefaultProps) => {
  const pageTemplateService = useRepository(PageTemplateService)

  return (
    <div css={_Gutter} onClick={() => pageTemplateService.setSideNavigationOpen(false)}>
      <div css={_Style}>{children}</div>
    </div>
  )
}

export default PageContentTemplate

const WithTab = ({
  children,
  navInfoList,
  className,
}: { navInfoList: NavInfo[] } & DefaultProps) => {
  const location = useLocation()
  const pageTemplateService = useRepository(PageTemplateService)

  // 표시할 탭이 하나도 없으면 tab-list-container 자체를 그리지 않는다 (빈 줄 노출 방지)
  const hasVisibleTabs = navInfoList?.some(Boolean)

  return (
    <div css={_Gutter} onClick={() => pageTemplateService.setSideNavigationOpen(false)}>
      <div css={_Style} className={className}>
        {hasVisibleTabs && (
          <div className="tab-list-container" data-testid="page-tab-list">
            {navInfoList.map((navInfo, index) => {
              if (!navInfo) return null

              // label(ReactNode)·importStatement(프리로드 함수)·isNew(비표준 prop)를 분리해 a 태그로 spread 되지 않게 한다.
              const { label, importStatement, isNew, ...linkProps } = navInfo

              return (
                <TabNavigationLink
                  className="tab-list-item"
                  key={
                    typeof navInfo.to === 'string'
                      ? navInfo.to
                      : Array.isArray(navInfo.matchUrl)
                        ? navInfo.matchUrl.join('|')
                        : navInfo.matchUrl ?? `tab-${index}`
                  }
                  onMouseEnter={() => preloadModule(importStatement)}
                  {...linkProps}
                >
                  <span className="tab-label" css={S.tabLabelWrapper}>
                    {label}
                    {isNew && (
                      <img
                        src={s3URL.mathflatAssetsBucket.graphic('graphic-new.svg')}
                        alt="new"
                        width={14}
                        height={14}
                      />
                    )}
                  </span>
                  {matchPath(location.pathname, {
                    path:
                      navInfo.matchUrl ?? (typeof navInfo.to === 'string' ? navInfo.to : undefined),
                    exact: navInfo.exact,
                  }) && (
                    <div className="underline-wrapper">
                      <div className="underline" />
                    </div>
                  )}
                </TabNavigationLink>
              )
            })}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

const Transparent = ({ children }: DefaultProps) => {
  const pageTemplateService = useRepository(PageTemplateService)

  return (
    <div css={_Gutter} onClick={() => pageTemplateService.setSideNavigationOpen(false)}>
      <div css={_Style} className="transparent">
        {children}
      </div>
    </div>
  )
}

const FullPage = ({ children }: DefaultProps) => {
  const pageTemplateService = useRepository(PageTemplateService)

  return (
    <div
      css={_FullPageStyle}
      className="full-page"
      onClick={() => pageTemplateService.setSideNavigationOpen(false)}
    >
      <div className="full-inner-wrapper">{children}</div>
    </div>
  )
}

const FullPageInner = ({ children, style }: DefaultProps) => {
  return (
    <div className="inner" style={style}>
      {children}
    </div>
  )
}

const Fallback = () => {
  return (
    <LnbPageLayout lnb={<div css={_SideSkeletonStyle} />}>
      <div css={_Gutter}>
        <div css={_Style} />
      </div>
    </LnbPageLayout>
  )
}

const _Gutter = css`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  padding: 16px;

  ${mediaQuery.large} {
    padding: 16px 24px;
  }
`

const S = {
  tabLabelWrapper: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
}

const _Style = css`
  display: flex;
  flex: auto;
  flex-direction: column;
  height: 100%;
  background: var(--White);
  max-width: 1920px;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  border-radius: var(--Radius-200);
  overflow: hidden;
  border: 1px solid var(--Colors-Gray-400);

  &.transparent {
    background: transparent;
    border: none;
  }

  .tab-list-container {
    display: flex;
    padding: 4px 24px 0 24px;
    border-bottom: 1px solid var(--Colors-Gray-300);
  }

  .tab-list-item {
    position: relative;
    z-index: 5;
    min-width: 120px;
    padding: 0 16px;
  }

  .tab-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .underline-wrapper {
    position: absolute;
    top: 0;
    left: 50%;
    width: 80px;
    height: 100%;
    transform: translateX(-50%);

    .underline {
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: var(--Colors-Gray-900);
    }
  }
`

const _FullPageStyle = css`
  display: flex;
  flex: auto;
  border: none;
  border-radius: 0;
  background: var(--Colors-Gray-200);
  overflow: auto;

  .full-inner-wrapper {
    display: flex;
    gap: 16px;
    width: 100%;
    max-width: 1920px;
    margin: 0 auto;
    padding: 16px 24px;
    overflow: auto;

    ${mediaQuery.large} {
      padding: 0;
    }
  }

  .inner {
    display: flex;
    border-radius: var(--Radius-200);
    height: max-content;
    width: 100%;
    overflow: hidden;
    border: 1px solid var(--Colors-Gray-400);

    ${mediaQuery.large} {
      border-radius: 0;
    }
  }

  ${mediaQuery.large} {
    padding: 0;
    border: none;
  }
`

const _SideSkeletonStyle = css`
  flex: 0 0 ${SIDE_NAVIGATION_WIDTH}px;
  height: 100%;
  background: var(--Colors-Gray-100);
  border-right: 1px solid var(--Colors-Gray-400);

  ${mediaQuery.large} {
    display: none;
  }
`

PageContentTemplate.WithTab = WithTab
PageContentTemplate.Transparent = Transparent
PageContentTemplate.FullPage = FullPage
PageContentTemplate.FullPageInner = FullPageInner
PageContentTemplate.Fallback = Fallback
